import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import SearchField from "@amzn/meridian/search-field";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import { useEffect, useMemo, useState } from "react";
import { TOAST_TIMEOUT } from "src/constants/constants";
import { useAppDispatch } from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import LoadingSpinner from "src/components/shared/loadingSpinner/loadingSpinner";
import PaginationWithDropdown from "src/components/shared/paginationWithDropdown/paginationWithDropdown";
import { DEFAULT_TABLE_PAGE_SIZE } from "src/constants/constants";
import { filterTableData, paginateTableData } from "src/utils/tableUtils";
import {
  FAILURE_REASON_CODES,
  REASON_CODE_LABELS,
  REPORT_REGION_ORDER,
  regionShortLabel,
  sortRows,
} from "./adminReportUtils";
import FailedCasesTable from "./components/failedCasesTable";
import LoadError from "./components/loadError";
import ReportPageHeader from "./components/reportPageHeader";
import TestCaseModal from "./components/testCaseModal";
import adminReportService from "./service/adminReportService";
import { FailedCase, ReasonCode, RegionCode } from "./types";

const FailedCases = () => {
  const dispatch = useAppDispatch();
  const [failedCases, setFailedCases] = useState<FailedCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [regionFilter, setRegionFilter] = useState<RegionCode | "all">("all");
  const [reasonFilter, setReasonFilter] = useState<ReasonCode | "all">("all");
  const [sortColumn, setSortColumn] = useState<keyof FailedCase>("region");
  const [sortDirection, setSortDirection] = useState("ascending");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [editing, setEditing] = useState<FailedCase>();
  const [removing, setRemoving] = useState<FailedCase>();
  const [isRemoving, setIsRemoving] = useState(false);

  const loadFailedCases = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      setFailedCases(await adminReportService.getFailures());
    } catch {
      setFailedCases([]);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFailedCases();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchField, regionFilter, reasonFilter, perPage]);

  const confirmRemove = async () => {
    if (!removing) return;
    setIsRemoving(true);
    try {
      await adminReportService.deleteTestCase(
        removing.region,
        removing.attachmentId,
      );
      setRemoving(undefined);
      loadFailedCases();
    } catch {
      dispatch(
        createToast({
          type: "error",
          message: "Failed to remove the test case",
          timeout: TOAST_TIMEOUT,
        }),
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const filteredCases = useMemo(
    () =>
      failedCases.filter(
        (failedCase) =>
          (regionFilter === "all" || failedCase.region === regionFilter) &&
          (reasonFilter === "all" || failedCase.reasonCode === reasonFilter),
      ),
    [failedCases, regionFilter, reasonFilter],
  );

  // Kept separate from pagination so the summary count reflects the search too.
  const matchingCases = sortRows(
    filterTableData({ tableData: filteredCases, searchField }),
    sortColumn,
    sortDirection,
  );

  const { paginatedTableData, totalPages } = paginateTableData({
    tableData: matchingCases,
    currentPage,
    pageSize: perPage,
  });

  const onSort = (field: keyof FailedCase) => {
    if (field === sortColumn) {
      setSortDirection(
        sortDirection === "ascending" ? "descending" : "ascending",
      );
    } else {
      setSortColumn(field);
      setSortDirection("ascending");
    }
    setCurrentPage(1);
  };

  return (
    <div className="admin-report">
      <ReportPageHeader />

      <div className="admin-report__body">
        <section className="admin-report__card">
          <div className="admin-report__filters">
            <div className="admin-report__filter admin-report__filter--search">
              <SearchField
                value={searchField}
                onChange={setSearchField}
                onSubmit={() => {}}
                placeholder="Search by question, document, test case ID or cited title..."
                size="small"
                width="100%"
              />
            </div>
            <div className="admin-report__filter">
              <Select
                value={regionFilter}
                onChange={setRegionFilter}
                size="small"
                width="100%"
                label="Region"
              >
                <SelectOption value="all" label="All regions" />
                {REPORT_REGION_ORDER.map((code) => (
                  <SelectOption
                    key={code}
                    value={code}
                    label={regionShortLabel(code)}
                  />
                ))}
              </Select>
            </div>
            <div className="admin-report__filter">
              <Select
                value={reasonFilter}
                onChange={setReasonFilter}
                size="small"
                width="100%"
                label="Reason"
              >
                <SelectOption value="all" label="All reasons" />
                {FAILURE_REASON_CODES.map((reasonCode) => (
                  <SelectOption
                    key={reasonCode}
                    value={reasonCode}
                    label={REASON_CODE_LABELS[reasonCode]}
                  />
                ))}
              </Select>
            </div>
          </div>

          <div className="admin-report__list-summary">
            <div className="admin-report__list-count">
              {`${matchingCases.length} failing test case${
                matchingCases.length === 1 ? "" : "s"
              }`}
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : hasError ? (
            <div className="admin-report__card-body">
              <LoadError
                subject="failing test cases"
                onRetry={loadFailedCases}
              />
            </div>
          ) : (
            <FailedCasesTable
              failedCases={paginatedTableData}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
              onEdit={setEditing}
              onRemove={setRemoving}
            />
          )}
        </section>

        {!isLoading && matchingCases.length > perPage && (
          <PaginationWithDropdown
            totalResults={matchingCases.length}
            totalPages={totalPages}
            currentPage={currentPage}
            perPage={perPage}
            setPerPage={setPerPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      {editing && (
        <TestCaseModal
          open
          onClose={() => setEditing(undefined)}
          region={editing.region}
          attachmentId={editing.attachmentId}
          expectedFileName={editing.expectedFileName}
          currentQuestion={editing.question}
          onSaved={loadFailedCases}
        />
      )}

      <Modal
        open={Boolean(removing)}
        onClose={() => setRemoving(undefined)}
        title="Remove Test Case"
        width="520px"
        bodySpacingInset="400"
      >
        <Column spacing="300">
          <Text>
            {`Remove ${removing?.testCaseId} from ${removing?.expectedFileName}?`}
          </Text>
          <Text type="b100" color="secondary">
            The document returns to the uncovered list, so nothing will check
            that it can be retrieved until a new question is added.
          </Text>
        </Column>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button
              type="secondary"
              size="small"
              onClick={() => setRemoving(undefined)}
            >
              Cancel
            </Button>
            <Button size="small" disabled={isRemoving} onClick={confirmRemove}>
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FailedCases;
